import { QueryFunctionContext } from '@tanstack/react-query'
import backendRequest from '../../../services/api/backendRequest'
import { DimensionQueryOperator } from '../../dimension/types/Dimension'
import { financialStatementsEndpoints } from '../api/financialStatementsEndpoints'
import { DimensionStetementsResponse, FinancialStatementsFilterParams, FinancialStatementsResponse } from '../types'
import { PeriodGroup } from '../../../types/periodGroup/PeriodGroup'
import { financialStatementsKeys } from '../queries/financialStatementsQueryKeys'
import { BudgetComment } from '../../budgetComments/types'

// Query function with object destructuring
export const fetchFinancialStatementData =
  (params: FinancialStatementsFilterParams) =>
  async ({
    queryKey: [{ companyId }],
    signal
  }: QueryFunctionContext<
    ReturnType<(typeof financialStatementsKeys)['statementData']>
  >): Promise<FinancialStatementsResponse> => {
    // Single endpoint returns all three statement types
    return backendRequest<FinancialStatementsResponse>({
      method: 'GET',
      url: financialStatementsEndpoints.getFinancialStatement(companyId),
      params,
      signal
    })
  }

export interface FetchDimensionStatementsParams {
  companyId: string
  dimensionIds: string[]
  statementRowId?: number
  accountCode?: number
  budgetingScenarioId?: number | null
  periodGroups?: PeriodGroup[]
}

export const fetchDimensionStatements = ({
  companyId,
  statementRowId,
  accountCode,
  dimensionIds,
  budgetingScenarioId,
  periodGroups
}: FetchDimensionStatementsParams) =>
  backendRequest<DimensionStetementsResponse>({
    method: 'GET',
    url: financialStatementsEndpoints.getFinancialStatement(companyId),
    params: {
      statementRowId,
      accountCode,
      dimensions: JSON.stringify(
        dimensionIds?.map(id => ({
          k: id,
          o: DimensionQueryOperator.has,
          v: id
        }))
      ),
      budgetingScenarioId,
      periodGroups: periodGroups?.map(({ id, startDate, endDate, budgetable }) => ({
        id,
        startDate,
        endDate,
        budgetable
      }))
    }
  })

export interface FetchCommentsParams {
  companyId: string
  budgetingScenarioId?: number | null
}

export const fetchComments = ({ companyId, budgetingScenarioId }: FetchCommentsParams) =>
  backendRequest<BudgetComment[]>({
    method: 'GET',
    url: financialStatementsEndpoints.getComments(companyId),
    params: {
      budgetingScenarioId
    }
  })

// Comment mutations
export interface CreateCommentParams {
  companyId: string
  comment: Omit<BudgetComment, 'id' | 'companyId' | 'userId' | 'createdAt' | 'updatedAt'>
  budgetingScenarioId?: number | null
}

export const createComment = ({ companyId, comment, budgetingScenarioId }: CreateCommentParams) =>
  backendRequest<BudgetComment>({
    method: 'POST',
    url: `/companies/${companyId}/budgeting/comments`,
    data: comment,
    params: {
      budgetingScenarioId
    }
  })

export interface UpdateCommentParams {
  companyId: string
  commentId: number
  comment: Partial<Omit<BudgetComment, 'id' | 'companyId' | 'userId' | 'createdAt' | 'updatedAt'>>
  budgetingScenarioId?: number | null
}

export const updateComment = ({ companyId, commentId, comment, budgetingScenarioId }: UpdateCommentParams) =>
  backendRequest<BudgetComment>({
    method: 'PUT',
    url: `/companies/${companyId}/budgeting/comments/${commentId}`,
    data: comment,
    params: {
      budgetingScenarioId
    }
  })

export interface DeleteCommentParams {
  companyId: string
  commentId: number
  budgetingScenarioId?: number | null
}

export const deleteComment = ({ companyId, commentId, budgetingScenarioId }: DeleteCommentParams) =>
  backendRequest<void>({
    method: 'DELETE',
    url: `/companies/${companyId}/budgeting/comments/${commentId}`,
    params: {
      budgetingScenarioId
    }
  })
